import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom';
import PageHeader from '../../components/pageHeader/PageHeader';
import PageWrapper from '../../components/pageComponents/PageWrapper';
import SearchContainer from '../../components/pageComponents/SearchContainer';
import PageTable from '../../components/pageComponents/PageTable';
import ActionsIcons from '../../components/atoms/ActionsIcons';
import ModalEnabled from '../../components/modalComponents/ModalEnabled';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { NavDropdown } from 'react-bootstrap';
import styled from "styled-components";
import { useManagePostRequest } from '../../hooks/useManagePostRequest/useManagePostRequest';
import { useGetRequest } from '../../hooks/useGetRequest/useGetRequest';
import { useGenerarDescargarEliminarArchivo } from '../../hooks/useGenerarDescargarEliminarArchivo/useGenerarDescargarEliminarArchivo';
import useDidMountEffect from '../../hooks/useDidMountEffect/useDidMountEffect';
import FormSelect from '../../components/atoms/Select';
import { initialAllOptionPromises, handleFind, usuarioLogueadoTieneAlgunoDeLosSiguientesRoles } from '../../helper/utils';
import { AuthContext } from '../../hooks/context/authContext';
import { ROLES, ESTAPAS_POSTULACION_TP } from '../../constants/hardCodedConstants';
import { listarConvocatoriasAPI, getTPEstadosAPI, exportarConvocatoriasAPI, getDetalleConvocatoriaAPI } from '../../consumers/backendApisUrls';
import NuevaConvocatoriaMasivo from './modales/NuevaConvocatoriaMasivo';
import NuevaConvocatoriaIndividual from './modales/NuevaConvocatoriaIndividual';
import DetalleConvocatoriaModal from './modales/DetalleConvocatoriaModal';
import ReasignarResponsableModal from './modales/ReasignarResponsableModal';
import CancelarConvocatoriaModal from './modales/CancelarConvocatoriaModal';
import RevisionFirmaGDH from './modales/RevisionFirmaGDH';
import ConfirmarEnvioComunicacionResultado from './modales/ConfirmarEnvioComunicacionResultado';

// dbo.Estado — TP_Convocatoria.IdEstado. Una convocatoria Masiva solo se edita
// mientras sigue en borrador; publicada queda congelada (regla de GDH, 18/09/2026).
// Individual queda fuera: nace Publicada y nunca visita el borrador, así que la regla
// la dejaría sin ninguna ventana de edición. El lambda valida lo mismo en
// validarConvocatoriaEditable; esto solo evita el viaje.
const ESTADO_CONVOCATORIA_BORRADOR = 66;
const ESTADO_CONVOCATORIA_CANCELADO = 25;

// Una convocatoria cancelada no se edita (el lambda también lo rechaza en
// validarConvocatoriaEditable: editar una Individual la devolvería a Publicada).
const esConvocatoriaEditable = (row) =>
    row?.idEstado !== ESTADO_CONVOCATORIA_CANCELADO
    && (row?.tipoModalidad === 'I' || row?.idEstado === ESTADO_CONVOCATORIA_BORRADOR);

// Indexado por IdEstado y no por el nombre: dbo.Estado guarda "En edición
// (borrador)" y "Proceso", y cualquier diferencia de tilde o de redacción dejaba
// el badge sin color sin que nadie se enterara. El id no se mueve.
const ESTADO_ESTILOS = {
    16: { background: "#fff3cd", color: "#997404" },  // Proceso
    19: { background: "#e2f6e9", color: "#1a7f4b" },  // Finalizado
    25: { background: "#f8d7da", color: "#a4343a" },  // Cancelado
    66: { background: "#f0f0f0", color: "#555" },     // En edición (borrador)
    67: { background: "#e3f2fd", color: "#0d6efd" },  // Publicado
};

// El texto sigue saliendo de desEstado, para no duplicar el catálogo acá.
const EstadoBadge = ({ idEstado, estado }) => (
    <span style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: "12px",
        fontSize: "0.85em",
        ...(ESTADO_ESTILOS[idEstado] ?? {})
    }}>
        {estado}
    </span>
);

const TIPO_PROCESO_OPTIONS = [
    { Value: "T", Text: "Traslado" },
    { Value: "P", Text: "Promoción" },
];

const TIPO_MODALIDAD_OPTIONS = [
    { Value: "M", Text: "Masivo" },
    { Value: "I", Text: "Individual" },
];

// Claves que alimentan el filtro (los catálogos de API se cargan con
// initialAllOptionPromises y quedan en `allFilters` como [{key, value}]);
// los filtros de Tipo/Modalidad usan opciones estáticas.
const INIT_KEYS = {
    estado: "IdEstado",
};

const formatearFecha = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleDateString("es-PE");
};

const GestionTrasladosYPromociones = () => {
    const history = useHistory();
    const { state } = useContext(AuthContext);
    const [isLoading, setIsLoading] = useState(true);
    const [searchCriteria, setSearchCriteria] = useState("");
    const [paginator, setPaginator] = useState({ CurrentPage: 1 });
    const [formValues, setFormValues] = useState({ Items: [], ExtraData: {}, selectedFilters: { IdEstado: [], TipoProceso: [], TipoModalidad: [], IdEmpleadoResponsable: [] } });
    const [allFilters, setAllFilters] = useState([]);
    const [modalEnabled, setModalEnabled] = useState({ isEnable: false });

    // TM-06/PM-10 (29/09/2026): mismo mitigador de doble clic/doble envío que ya
    // usa BandejaPostulantes.jsx para el ícono de avión — acá solo hace falta la
    // variante individual (esta grilla no tiene envío en lote).
    const [idsEnviandoComunicacion, setIdsEnviandoComunicacion] = useState([]);
    const handleIniciarEnvioComunicacion = (idPostulante) => {
        setIdsEnviandoComunicacion((prev) => [...new Set([...prev, idPostulante])]);
    };
    const handleFinalizarEnvioComunicacion = (idPostulante) => {
        setIdsEnviandoComunicacion((prev) => prev.filter((id) => id !== idPostulante));
    };

    const [managePostRequest] = useManagePostRequest();
    const [executeGet] = useGetRequest();
    const [generarDescargarEliminarArchivo] = useGenerarDescargarEliminarArchivo();

    const initialPromises = () => [
        executeGet(getTPEstadosAPI()),
    ];

    // CA-02: las opciones de "Responsable" se derivan de las filas cargadas.
    //
    // NO sirve Datos/GetResponsableEmpleadoWithoutReserverdPeopleWordType para
    // alimentar este dropdown, ni pasandole { Criteria: "" }: el helper corta antes
    // de consultar (DopdownHelper.cs:1628 `if (string.IsNullOrEmpty(criteria))
    // return result;`), asi que devuelve lista vacia. Es un endpoint de
    // autocompletado, no un catalogo.
    //
    // Mismo patron que SeleccionarVacanteModal, que arma sus filtros desde las filas.
    const responsableOptions = useMemo(() => {
        const vistos = new Map();
        for (const row of formValues.Items ?? []) {
            if (row?.idEmpleadoResponsable && !vistos.has(row.idEmpleadoResponsable)) {
                vistos.set(row.idEmpleadoResponsable, {
                    Value: row.idEmpleadoResponsable,
                    Text: row.nombreEmpleadoResponsable ?? `Empleado ${row.idEmpleadoResponsable}`,
                });
            }
        }
        return [...vistos.values()].sort((a, b) => a.Text.localeCompare(b.Text));
    }, [formValues.Items]);

    const manageSearch = async () => {
        const api = listarConvocatoriasAPI();
        const payload = getPayload();
        await managePostRequest(api, payload, successManageSearchCallback);
    };

    const getPayload = () => {
        const sf = formValues.selectedFilters ?? {};
        // 29/09/2026 (a pedido de Jody): el backend ahora filtra por selección múltiple
        // (List<>) — antes solo aceptaba un único valor y acá se mandaba nomás el primero
        // de la selección, así que elegir más de una opción no filtraba nada distinto de
        // elegir solo la primera (mismo fix que BandejaPostulantes.jsx).
        return {
            NombreConvocatoria: searchCriteria,
            Page: paginator.CurrentPage,
            PageSize: 10,
            IdEstado: sf.IdEstado ?? [],
            TipoProceso: sf.TipoProceso ?? [],
            TipoModalidad: sf.TipoModalidad ?? [],
            IdEmpleadoResponsable: sf.IdEmpleadoResponsable ?? [],
        };
    };

    const successManageSearchCallback = ({ data }) => {
        const { Items, ExtraData, TotalItems, FirsItemPage, LastItemPage, TotalPages, CurrentPage } = data;
        setFormValues(lastData => ({ ...lastData, Items, ExtraData }));
        setPaginator({ TotalItems, FirsItemPage, LastItemPage, TotalPages, CurrentPage });
    };

    const handleFilterSelected = (res, key) => {
        setFormValues(lastData => ({ ...lastData, selectedFilters: { ...lastData.selectedFilters, [key]: res } }));
    };

    const handleCleanSearch = () => {
        setFormValues(lastData => ({ ...lastData, selectedFilters: { IdEstado: [], TipoProceso: [], TipoModalidad: [], IdEmpleadoResponsable: [] } }));
    };

    const init = async () => {
        setIsLoading(true);
        try {
            await initialAllOptionPromises(initialPromises, Object.values(INIT_KEYS), setAllFilters);
            await manageSearch();
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        init();
    }, []);

    useDidMountEffect(() => {
        manageSearch();
    }, [paginator.CurrentPage, formValues.selectedFilters]);

    const handleNuevaConvocatoriaMasivo = () => {
        setModalEnabled({ isEnable: true, component: NuevaConvocatoriaMasivo, data: {} });
    };

    const handleNuevaConvocatoriaIndividual = () => {
        setModalEnabled({ isEnable: true, component: NuevaConvocatoriaIndividual, data: {} });
    };

// Descarga un Excel con las convocatorias según los filtros vigentes de la
// grilla (se exportan todas las coincidencias, no solo la página actual)
    const handleDescargarExcel = async () => {
        const api = exportarConvocatoriasAPI();
        const payload = { ...getPayload(), Page: 1, PageSize: 10000 };
        await generarDescargarEliminarArchivo(api, payload);
    };

// Visualizar el detalle de la convocatoria (GET GetConvocatoria/{id})
    const handleVerDetalle = (row) => {
        setModalEnabled({ isEnable: true, component: DetalleConvocatoriaModal, data: { id: row.idConvocatoria } });
    };

// Editar: para Masivo se consulta GetDetalleConvocatoria (AGA266) para reabrir
// el wizard en la Etapa guardada; ademas se pasa el `row`, que trae mas campos
// que el detalle (cargo, regiones, sedes, antiguedad, responsable) y las fechas
// con hora. Individual no tiene wizard por pasos: se precarga solo del `row`.
    const handleEditar = async (row) => {
        if (row?.tipoModalidad === "I") {
            setModalEnabled({ isEnable: true, component: NuevaConvocatoriaIndividual,
                data: { convocatoria: row, modoEdicion: true } });
            return;
        }

        const response = await executeGet(getDetalleConvocatoriaAPI(row.idConvocatoria));
        setModalEnabled({ isEnable: true, component: NuevaConvocatoriaMasivo,
            data: { convocatoria: row, modoEdicion: true, detalleConvocatoria: response?.data ?? null } });
    };

// Copiar abre el modal en modo creación, precargado desde el `row` para
// clonar la configuración (crea una convocatoria nueva a partir de ella).
// No se pasa `detalleConvocatoria`: la copia siempre arranca en el paso 1.
    const handleCopiar = (row) => {
        const component = row?.tipoModalidad === "I" ? NuevaConvocatoriaIndividual : NuevaConvocatoriaMasivo;
        setModalEnabled({ isEnable: true, component, data: { convocatoria: row, modoEdicion: false } });
    };

// Reasigna el responsable de la convocatoria (Pieza C).
    const handleReasignarResponsable = (row) => {
        setModalEnabled({ isEnable: true, component: ReasignarResponsableModal, data: { row } });
    };

// Cancelar una Individual con asignación confirmada: pide confirmación en un modal
// (ver CancelarConvocatoriaModal). Al confirmar libera la vacante y la deja Cancelada.
    const handleCancelar = (row) => {
        setModalEnabled({ isEnable: true, component: CancelarConvocatoriaModal, data: { row } });
    };

// 07/09/2026: lleva a BandejaPostulantes.jsx (la grilla de postulantes ya
// postulados). Se le pasa idConvocatoria/nombreConvocatoria por location.state
// para que la bandeja pueda mostrar de qué convocatoria se trata; el backend
// todavia no puede filtrar por convocatoria porque ListarPostulantesByConvocatoriaViewModel
// no tiene un campo IdConvocatoria (se lo comento a Jody por separado).
    const handleVerPostulantes = (row) => {
        history.push('/bandejaPostulantes', {
            idConvocatoria: row.idConvocatoria,
            nombreConvocatoria: row.nombreConvocatoria
        });
    }

    // HU-TR-07 CA-10: Individual no tiene "Ver Postulantes" (ver mas abajo), asi
    // que el icono de revisar firma va directo en esta grilla en vez de en
    // BandejaPostulantes.jsx — mismo modal que usa Masivo (24/09/2026).
    const handleRevisionFirma = (row) => {
        setModalEnabled({ isEnable: true, component: RevisionFirmaGDH,
            data: { postulante: { idPostulante: row.idPostulante, nombreConvocatoria: row.nombreConvocatoria } } });
    }

    // TM-06/PM-10 (29/09/2026, a pedido de Jody): confirmación de cierre — a
    // demanda, mismo modal que usa el ícono de avión en BandejaPostulantes.jsx
    // (Masivo). "Resultado" se arma acá porque esta grilla no trae
    // desEstadoPostulacion (solo el id, ver ConvocatoriaResponse.java) y el icono
    // solo aparece en el único caso que representa (cierre confirmado).
    const handleEnviarComunicacionResultado = (row) => {
        setModalEnabled({
            isEnable: true,
            component: ConfirmarEnvioComunicacionResultado,
            data: {
                postulante: {
                    idPostulante: row.idPostulante,
                    nombrePostulante: row.nombreEmpleado,
                    desEstado: row.tipoProceso === 'T' ? 'Traslado cerrado' : 'Promoción cerrada',
                },
                nombreConvocatoria: row.nombreConvocatoria,
                onIniciarEnvio: handleIniciarEnvioComunicacion,
                onFinalizarEnvio: handleFinalizarEnvioComunicacion,
            },
        });
    }

    const items = formValues.Items || [];

    const HEADERS = [
        { col: "col-sm-1", label: "Fecha de Registro" },
        { col: "col-sm-3", label: "Nombre de la convocatoria" },
        {
            col: "col-sm-1", label:
                <div className='d-flex justify-content-center'>
                    <FormSelect description="Tipo"
                                options={TIPO_PROCESO_OPTIONS}
                                selectedOptions={formValues.selectedFilters?.TipoProceso ?? []}
                                setSelectedOptions={(res) => handleFilterSelected(res, "TipoProceso")} />
                </div>
        },
        {
            col: "col-sm-1", label:
                <div className='d-flex justify-content-center'>
                    <FormSelect description="Modalidad"
                                options={TIPO_MODALIDAD_OPTIONS}
                                selectedOptions={formValues.selectedFilters?.TipoModalidad ?? []}
                                setSelectedOptions={(res) => handleFilterSelected(res, "TipoModalidad")} />
                </div>
        },
        { col: "col-sm-1", label: "Fecha inicio" },
        { col: "col-sm-1", label: "Fecha término" },
        { col: "col-sm-1", label: "Nro. de Postulantes" },
        {
            col: "col-sm-2", label:
                <div className='d-flex justify-content-center'>
                    <FormSelect description="Responsable"
                                options={responsableOptions}
                                selectedOptions={formValues.selectedFilters?.IdEmpleadoResponsable ?? []}
                                setSelectedOptions={(res) => handleFilterSelected(res, "IdEmpleadoResponsable")} />
                </div>
        },
        {
            col: "col-sm-1", label:
                <div className='d-flex justify-content-center'>
                    <FormSelect description="Estado"
                                options={handleFind(allFilters, INIT_KEYS.estado)}
                                selectedOptions={formValues.selectedFilters?.IdEstado ?? []}
                                setSelectedOptions={(res) => handleFilterSelected(res, "IdEstado")} />
                </div>
        },
        { col: "col-sm-1", label: "Acciones" },
    ];

    return (
        <div>
            <ModalEnabled modalEnabled={modalEnabled} setModalEnabled={setModalEnabled} refreshPage={manageSearch} />
            <PageWrapper isLoading={isLoading}>
                <PageHeader title="Gestión de Traslados y Promociones" />
                <SearchContainer
                    searchCriteria={searchCriteria}
                    setSearchCriteria={setSearchCriteria}
                    setPaginator={setPaginator}
                    manageSearch={manageSearch}
                    addCleanSearch={handleCleanSearch}>

                    <ButtonsWrapper>
                        <div className="d-flex" style={{ gap: '8px' }}>
                            <NavDropdown
                                className="generic-button-dropdown font-gothan-light"
                                title={<span className='generic-button-dropdown-title'>Nueva convocatoria</span>}>
                                <span className='dropdown-item'
                                      style={{ cursor: 'pointer' }}
                                      onClick={handleNuevaConvocatoriaMasivo}>Masivo</span>
                                <span className='dropdown-item'
                                      style={{ cursor: 'pointer' }}
                                      onClick={handleNuevaConvocatoriaIndividual}>Individual</span>
                            </NavDropdown>
                            <button type='button'
                                    className='generic-button-buscador'
                                    onClick={handleDescargarExcel}>Descargar excel</button>
                        </div>
                    </ButtonsWrapper>
                </SearchContainer>

                <PageTable headers={HEADERS} paginator={paginator} setPaginator={setPaginator}>
                    <tbody className="listar-tbody">
                    {items.map((row) => (
                        <tr key={row.idConvocatoria}>
                            <td className="text-center">{formatearFecha(row.fechaCrea)}</td>
                            <td className="text-center">{row.nombreConvocatoria}</td>
                            <td className="text-center">{row.tipoProceso === 'T' ? 'Traslado' : row.tipoProceso === 'P' ? 'Promoción' : row.tipoProceso}</td>
                            <td className="text-center">{row.tipoModalidad === 'M' ? 'Masivo' : row.tipoModalidad === 'I' ? 'Individual' : row.tipoModalidad}</td>
                            <td className="text-center">{formatearFecha(row.fechaHoraInicio)}</td>
                            <td className="text-center">{formatearFecha(row.fechaHoraFin)}</td>
                            <td className="text-center">{row.nroPostulantes ?? 0}</td>
                            <td className="text-center">{row.nombreEmpleadoResponsable}</td>
                            <td className="text-center"><EstadoBadge idEstado={row.idEstado} estado={row.desEstado} /></td>
                            <td className="text-center">
                                <ActionsIcons>
                                    {formValues.ExtraData?.PuedeVerDetalle &&
                                        <FontAwesomeIcon icon="id-card" size="md"
                                                         className="hand-pointer"
                                                         title="Ver detalle"
                                                         onClick={() => handleVerDetalle(row)} />
                                    }
                                    {formValues.ExtraData?.PuedeReasignarResponsable &&
                                        <FontAwesomeIcon icon="male" size="md"
                                                         className="hand-pointer"
                                                         title="Reasignar Responsable"
                                                         onClick={() => handleReasignarResponsable(row)} />
                                    }
                                    {formValues.ExtraData?.PuedeEditarConvocatoria && (
                                        esConvocatoriaEditable(row)
                                            ? <FontAwesomeIcon icon="edit" size="md"
                                                              className="hand-pointer black-color"
                                                              title="Editar"
                                                              onClick={() => handleEditar(row)} />
                                            // Se deja visible pero inerte: si se ocultara,
                                            // GDH no entendería por qué le desapareció.
                                            : <FontAwesomeIcon icon="edit" size="md"
                                                              style={{ opacity: 0.35, cursor: "not-allowed" }}
                                                              title={row.idEstado === ESTADO_CONVOCATORIA_CANCELADO
                                                                  ? "La convocatoria fue cancelada y no admite modificaciones"
                                                                  : "La convocatoria ya fue publicada y no admite modificaciones"} />
                                    )}
                                    {/* Cancelar: solo Individual con asignación confirmada que
                                        no esté cancelada ni cerrada — la regla viene calculada del
                                        lambda (row.puedeCancelar), no se duplica acá. Mismo
                                        permiso que Editar. */}
                                    {formValues.ExtraData?.PuedeEditarConvocatoria && row.puedeCancelar &&
                                        <FontAwesomeIcon icon="ban" size="md"
                                                         className="hand-pointer"
                                                         title="Cancelar convocatoria"
                                                         onClick={() => handleCancelar(row)} />
                                    }
                                    {/* Individual no tiene "bandeja": GDH ya eligió al único
                                        colaborador al crearla (ver insertarPostulante en
                                        ProcesoRepositoryWrite.java) — una lista de un solo
                                        elemento no aporta nada, así que el ícono no se ofrece. */}
                                    {formValues.ExtraData?.PuedeVerPostulantes && row.tipoModalidad !== 'I' &&
                                        <FontAwesomeIcon icon="user-friends" size=""
                                                         className="hand-pointer" title="Ver Postulantes"
                                                         onClick={() => handleVerPostulantes(row)}
                                        />
                                    }
                                    {/* HU-TR-07 CA-10: Individual no pasa por "Ver Postulantes"
                                        (arriba), asi que el icono de revisar firma va directo
                                        aca cuando el unico postulante llega a etapa 8 (24/09/2026). */}
                                    {formValues.ExtraData?.PuedeVerPostulantes
                                        && row.tipoModalidad === 'I'
                                        && row.idEstado !== ESTADO_CONVOCATORIA_CANCELADO
                                        && row.idEtapaPostulacion === ESTAPAS_POSTULACION_TP.FIRMA_CIERRE &&
                                        <FontAwesomeIcon icon="file-signature" size="md"
                                                         className="hand-pointer" title="Revisar firma del colaborador"
                                                         onClick={() => handleRevisionFirma(row)}
                                        />
                                    }
                                    {/* TM-06/PM-10 (29/09/2026): mismo criterio que "Revisar
                                        firma" arriba — Individual no pasa por BandejaPostulantes,
                                        asi que el icono de avion va directo aca. */}
                                    {formValues.ExtraData?.PuedeVerPostulantes
                                        && row.tipoModalidad === 'I'
                                        && row.puedeEnviarComunicacion && (
                                        idsEnviandoComunicacion.includes(row.idPostulante)
                                            ? <FontAwesomeIcon icon="spinner" spin size="md" title="Enviando..." />
                                            : <FontAwesomeIcon icon="paper-plane" size="md"
                                                                className="hand-pointer"
                                                                title="Enviar comunicación de resultado"
                                                                onClick={() => handleEnviarComunicacionResultado(row)}
                                            />
                                    )}
                                    {formValues.ExtraData?.PuedeCopiarConvocatoria &&
                                        <FontAwesomeIcon icon="copy" size="lg"
                                                         className="hand-pointer black-color"
                                                         title="Copiar"
                                                         onClick={() => handleCopiar(row)} />
                                    }
                                </ActionsIcons>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </PageTable>
            </PageWrapper>
        </div>
    )
}

export default GestionTrasladosYPromociones

const ButtonsWrapper = styled.div`
    display: block;
    @media (max-width: 796px) {
        display: none;
    }
`;